import { DefaultRootState } from 'react-redux';

import { SettingsConfig } from 'utils/constants/dynamicSettings';
import type { Methods } from 'utils/constants/methods';

import { ModelData as MagritteModelData } from '@hh.ru/magritte-ui';

import { ModelData } from 'bloko/common/tree/types';

import { createAutoGeneratedReducer, makeSetStoreField } from 'store/createReducer';
import { CampaignTabStatus } from 'store/models/dashboard';

const SHOW_LOW_BALANCE_INFO = 'showLowBalanceInfo';
const CAMPAIGN_PAGE = 'campaignPage';
const CAMPAIGN_TABS_STATUS = 'campaignTabsStatus';
const CHECKED_STORAGE_ACCOUNT_ID = 'checkedStorageAccountId';
const COUNTS_CAMPAIGN = 'countsCampaign';
const CRM_ACTIVITIES = 'crmActivities';
const LOAD_PAGE = 'loadPage';
export const PROF_ROLES = 'profRoles';
export const REGIONS = 'regions';
const X_REQUEST_ID = 'xRequestId';
const DYNAMIC_SETTINGS = 'dynamicSettings';
const SCROLL_TO_MY_ADS = 'scrollToMyAds';
const EXIST_ADVERTISING_CAMPAIGNS = 'existAdvertisingCampaigns';
const HH_AREAS = 'hhAreas';
export const EXPERIENCE = 'experience';
export const SCHEDULE = 'schedule';
export const DRIVER_LICENSE_TYPE = 'driverLicenseType';
const FETCH_CACHE_SETTINGS = 'fetchCacheSettings';
const USER_DROPDOWN_OPEN = 'userDropdownShowState';
const CREATE_AD_FORM_OFFSET_TOP = 'createAdFormOffsetTop';
const UPDATE_CAMPAIGN_STATUS_LOADING = 'updateCampaignStatusLoading';
const IS_VACANCY_FOREIGN_AREA = 'isVacancyForeignArea';
const SHOULD_SEND_UXFB_EVENT = 'shouldSendUXFBEvent';
const LEADS_COUNT = 'leadsCount';
const ERIR_CLASSIFICATION_CATALOG = 'erirClassificationCatalog';
const CAMPAIGN_ARCHIVE_MODAL_SHOW = 'campaignArchiveModalShow';
const AMOUNTS = 'amounts';
const IS_HELP_MODAL_VISIBLE = 'isHelpModalVisible';
const CHATIK_UNREAD_SUPPORT_COUNT = 'chatikUnreadSupportCount';
const IS_WEBINAR_BANNER_VISIBLE = 'isWebinarBannerVisible';
const IS_YANDEX_VIDEO_BANNER_VISIBLE = 'isYandexVideoBannerVisible';
const AVAILABLE_PROMOS = 'availablePromos';

interface FetchCacheSettings {
    fieldsToInvalidate: Methods[];
}

export interface CountsCampaign {
    [CampaignTabStatus.Active]: number;
    [CampaignTabStatus.Off]: number;
}

export interface CrmActivity {
    crmActivityTypeId: number;
    dailySent: number;
    dailyTotal: number;
    enabled: boolean;
    monthlySent: number;
    monthlyTotal: number;
    title: string;
    weeklySent: number;
    weeklyTotal: number;
}

export interface DriverLicenseType {
    name: string;
}

export interface Experience {
    id: number;
    name: string;
    translation: string;
}

export interface RegionBE {
    id: number;
    name: string;
    areas: RegionBE[];
}

export interface Region {
    id: string;
    name: string;
    areas: Region[];
}

export interface ProfRoles {
    id: number;
    name: string;
    professionalRoles?: ProfRoles[];
}

export interface Schedule {
    id: number;
    name: string;
    translation: string;
}

export interface Amounts {
    minAmount: number;
    recommendedAmount: number;
    suggestedAmounts: number[];
}

interface AvailablePromosItem {
    maxBonusAmount: number;
    startDate: string;
    endDate: string;
    sumPercent: number;
    termsLink?: string;
}

export type AvailablePromos = Record<string, AvailablePromosItem> | null;

const INITIAL_AMOUNTS: Amounts = {
    minAmount: 0,
    recommendedAmount: 0,
    suggestedAmounts: [],
};

const typedReducers = {
    [CAMPAIGN_PAGE]: createAutoGeneratedReducer<number>(0, CAMPAIGN_PAGE),
    [CREATE_AD_FORM_OFFSET_TOP]: createAutoGeneratedReducer<number>(0, CREATE_AD_FORM_OFFSET_TOP),
    [CAMPAIGN_TABS_STATUS]: createAutoGeneratedReducer<CampaignTabStatus>(
        CampaignTabStatus.Active,
        CAMPAIGN_TABS_STATUS
    ),
    [CHECKED_STORAGE_ACCOUNT_ID]: createAutoGeneratedReducer<boolean>(false, CHECKED_STORAGE_ACCOUNT_ID),
    [COUNTS_CAMPAIGN]: createAutoGeneratedReducer<CountsCampaign>(
        {
            [CampaignTabStatus.Active]: 0,
            [CampaignTabStatus.Off]: 0,
        },
        COUNTS_CAMPAIGN
    ),
    [CRM_ACTIVITIES]: createAutoGeneratedReducer<CrmActivity[]>([], CRM_ACTIVITIES),
    [DRIVER_LICENSE_TYPE]: createAutoGeneratedReducer<DriverLicenseType[]>([], DRIVER_LICENSE_TYPE),
    [DYNAMIC_SETTINGS]: createAutoGeneratedReducer<Partial<SettingsConfig>>(
        {} as Partial<SettingsConfig>,
        DYNAMIC_SETTINGS
    ),
    [EXIST_ADVERTISING_CAMPAIGNS]: createAutoGeneratedReducer<boolean>(false, EXIST_ADVERTISING_CAMPAIGNS),
    [EXPERIENCE]: createAutoGeneratedReducer<Experience[]>([], EXPERIENCE),
    [FETCH_CACHE_SETTINGS]: createAutoGeneratedReducer<FetchCacheSettings>(
        { fieldsToInvalidate: [] },
        FETCH_CACHE_SETTINGS
    ),
    [HH_AREAS]: createAutoGeneratedReducer<ModelData[]>([], HH_AREAS),
    [IS_VACANCY_FOREIGN_AREA]: createAutoGeneratedReducer<boolean>(false, IS_VACANCY_FOREIGN_AREA),
    [LOAD_PAGE]: createAutoGeneratedReducer<boolean>(true, LOAD_PAGE),
    [PROF_ROLES]: createAutoGeneratedReducer<ProfRoles[]>([], PROF_ROLES),
    [REGIONS]: createAutoGeneratedReducer<Region[]>([], REGIONS),
    [SCHEDULE]: createAutoGeneratedReducer<Schedule[]>([], SCHEDULE),
    [SCROLL_TO_MY_ADS]: createAutoGeneratedReducer<boolean>(false, SCROLL_TO_MY_ADS),
    [SHOW_LOW_BALANCE_INFO]: createAutoGeneratedReducer<boolean>(false, SHOW_LOW_BALANCE_INFO),
    [UPDATE_CAMPAIGN_STATUS_LOADING]: createAutoGeneratedReducer<boolean>(false, UPDATE_CAMPAIGN_STATUS_LOADING),
    [USER_DROPDOWN_OPEN]: createAutoGeneratedReducer<boolean>(false, USER_DROPDOWN_OPEN),
    [X_REQUEST_ID]: createAutoGeneratedReducer<string>('', X_REQUEST_ID),
    [SHOULD_SEND_UXFB_EVENT]: createAutoGeneratedReducer<boolean>(false, SHOULD_SEND_UXFB_EVENT),
    [LEADS_COUNT]: createAutoGeneratedReducer<number>(0, LEADS_COUNT),
    [ERIR_CLASSIFICATION_CATALOG]: createAutoGeneratedReducer<MagritteModelData[]>([], ERIR_CLASSIFICATION_CATALOG),
    [CAMPAIGN_ARCHIVE_MODAL_SHOW]: createAutoGeneratedReducer<boolean>(false, CAMPAIGN_ARCHIVE_MODAL_SHOW),
    [AMOUNTS]: createAutoGeneratedReducer<Amounts>(INITIAL_AMOUNTS, AMOUNTS),
    [IS_HELP_MODAL_VISIBLE]: createAutoGeneratedReducer<boolean>(false, IS_HELP_MODAL_VISIBLE),
    [CHATIK_UNREAD_SUPPORT_COUNT]: createAutoGeneratedReducer<number>(0, CHATIK_UNREAD_SUPPORT_COUNT),
    [IS_WEBINAR_BANNER_VISIBLE]: createAutoGeneratedReducer<boolean>(false, IS_WEBINAR_BANNER_VISIBLE),
    [IS_YANDEX_VIDEO_BANNER_VISIBLE]: createAutoGeneratedReducer<boolean>(false, IS_YANDEX_VIDEO_BANNER_VISIBLE),
    [AVAILABLE_PROMOS]: createAutoGeneratedReducer<AvailablePromos>(null, AVAILABLE_PROMOS),
};

export default typedReducers;

// ----------------------- setters ----------------------------------------
export const setScrollToAds = makeSetStoreField(SCROLL_TO_MY_ADS);
export const setRegions = makeSetStoreField(REGIONS);
export const setProfRoles = makeSetStoreField(PROF_ROLES);
export const setPage = makeSetStoreField(CAMPAIGN_PAGE);
export const setCurrentStatus = makeSetStoreField(CAMPAIGN_TABS_STATUS);
export const setCountsCampaign = makeSetStoreField(COUNTS_CAMPAIGN);
export const setLoadPage = makeSetStoreField(LOAD_PAGE);
export const setDynamicSettings = makeSetStoreField(DYNAMIC_SETTINGS);
export const setCheckedStorageAccountId = makeSetStoreField(CHECKED_STORAGE_ACCOUNT_ID);
export const setExistAdvertisingCampaigns = makeSetStoreField(EXIST_ADVERTISING_CAMPAIGNS);
export const setHhAreas = makeSetStoreField(HH_AREAS);
export const setDriverLicenseType = makeSetStoreField(DRIVER_LICENSE_TYPE);
export const setExperience = makeSetStoreField(EXPERIENCE);
export const setSchedule = makeSetStoreField(SCHEDULE);
export const setCrmActivities = makeSetStoreField(CRM_ACTIVITIES);
export const setShowLowBalanceInfo = makeSetStoreField(SHOW_LOW_BALANCE_INFO);
export const setXRequestId = makeSetStoreField(X_REQUEST_ID);
export const setFetchCacheSettings = makeSetStoreField(FETCH_CACHE_SETTINGS);
export const setUserDropdownOpen = makeSetStoreField(USER_DROPDOWN_OPEN);
export const setCreateAdFormOffsetTop = makeSetStoreField(CREATE_AD_FORM_OFFSET_TOP);
export const setUpdateCampaignStatusLoading = makeSetStoreField(UPDATE_CAMPAIGN_STATUS_LOADING);
export const setIsVacancyForeignArea = makeSetStoreField(IS_VACANCY_FOREIGN_AREA);
export const setShouldSendUXFBEvent = makeSetStoreField(SHOULD_SEND_UXFB_EVENT);
export const setLeadsCount = makeSetStoreField(LEADS_COUNT);
export const setErirClassificationCatalog = makeSetStoreField(ERIR_CLASSIFICATION_CATALOG);
export const setCampaignArchiveModalShow = makeSetStoreField(CAMPAIGN_ARCHIVE_MODAL_SHOW);
export const setAmounts = makeSetStoreField(AMOUNTS);
export const setIsHelpModalVisible = makeSetStoreField(IS_HELP_MODAL_VISIBLE);
export const setChatikUnreadSupportCount = makeSetStoreField(CHATIK_UNREAD_SUPPORT_COUNT);
export const setIsWebinarBannerVisible = makeSetStoreField(IS_WEBINAR_BANNER_VISIBLE);
export const setIsYandexVideoBannerVisible = makeSetStoreField(IS_YANDEX_VIDEO_BANNER_VISIBLE);
export const setAvailablePromos = makeSetStoreField(AVAILABLE_PROMOS);
// ---------------------- /setters --------------------------------------

// ----------------------- selectors --------------------------------------

export const selectProfessions = (state: DefaultRootState): ProfRoles[] => state[PROF_ROLES];

export const selectFetchCacheSettings = (state: DefaultRootState): FetchCacheSettings => state[FETCH_CACHE_SETTINGS];
export const selectUserDropdownOpen = (state: DefaultRootState): boolean => state[USER_DROPDOWN_OPEN];

export const selectCreateAdFormOffsetTop = (state: DefaultRootState): number => state[CREATE_AD_FORM_OFFSET_TOP];
export const selectUpdateCampaignStatusLoading = (state: DefaultRootState): boolean =>
    state[UPDATE_CAMPAIGN_STATUS_LOADING];
export const selectRegions = (state: DefaultRootState): Region[] => state[REGIONS];
export const selectIsVacancyForeignArea = (state: DefaultRootState): boolean => state[IS_VACANCY_FOREIGN_AREA];
export const selectShouldSendUXFBEvent = (state: DefaultRootState): boolean => state[SHOULD_SEND_UXFB_EVENT];
export const selectLeadsCount = (state: DefaultRootState): number => state[LEADS_COUNT];
export const selectErirClassificationCatalog = (state: DefaultRootState): MagritteModelData[] =>
    state[ERIR_CLASSIFICATION_CATALOG];
export const selectCampaignArchiveModalShow = (state: DefaultRootState): boolean => state[CAMPAIGN_ARCHIVE_MODAL_SHOW];
export const selectAmounts = (state: DefaultRootState): Amounts => state[AMOUNTS];
export const selectIsHelpModalVisible = (state: DefaultRootState): boolean => state[IS_HELP_MODAL_VISIBLE];
export const selectChatikUnreadSupportCount = (state: DefaultRootState): number => state[CHATIK_UNREAD_SUPPORT_COUNT];
export const selectIsWebinarBannerVisible = (state: DefaultRootState): boolean => state[IS_WEBINAR_BANNER_VISIBLE];
export const selectIsYandexVideoBannerVisible = (state: DefaultRootState): boolean =>
    state[IS_YANDEX_VIDEO_BANNER_VISIBLE];
export const selectAvailablePromos = (state: DefaultRootState): AvailablePromos => state[AVAILABLE_PROMOS];
// ----------------------- /selectors --------------------------------------
